import { type ElementType, type ReactNode, useRef } from 'react';

import { useInView } from '@/hooks/use-in-view';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE, RISE_DISTANCE } from '@/lib/motion-tokens';
import { cn } from '@/lib/utils';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

export interface ScrollRevealProps {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  /** Direction the content travels *from*. Default 'up'. */
  from?: Direction;
  /** Distance travelled (px). Default 28. */
  distance?: number;
  duration?: number;
  delay?: number;
  /**
   * When set, direct children are revealed in sequence with this stagger
   * (seconds). When omitted, the element reveals as one block.
   */
  stagger?: number;
  /** Fraction of viewport height the top must pass to reveal (default 0.85). */
  threshold?: number;
  once?: boolean;
}

const OFFSET: Record<Direction, { x?: number; y?: number }> = {
  up: { y: 1 },
  down: { y: -1 },
  left: { x: 1 },
  right: { x: -1 },
  none: {},
};

/**
 * The workhorse content reveal: a soft rise-and-fade as the element scrolls
 * into view. Pass `stagger` to sequence direct children (cards, list items).
 *
 * Reveal is driven by the rect-based {@link useInView} hook rather than GSAP
 * ScrollTrigger: ScrollTrigger's from-state (`autoAlpha:0`) can be stranded
 * `visibility:hidden` by a `refresh()`, a programmatic jump, a fast flick, or an
 * element already in view on load — which left whole sections blank. This hook
 * fires reliably in all those cases, and content is never hidden from
 * reduced-motion users.
 */
export function ScrollReveal({
  children,
  as: Tag = 'div',
  className,
  from = 'up',
  distance = RISE_DISTANCE,
  duration = DURATION.base,
  delay = 0,
  stagger,
  threshold = 0.85,
  once = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once, threshold, disabled: reduced });

  useGSAP(
    () => {
      if (reduced || !ref.current) return;
      const sign = OFFSET[from];
      const targets =
        stagger != null ? (gsap.utils.toArray(ref.current.children) as HTMLElement[]) : ref.current;
      const fromVars = {
        autoAlpha: 0,
        x: (sign.x ?? 0) * distance,
        y: (sign.y ?? 0) * distance,
      };

      if (!inView) {
        // Hold the hidden start state until the element enters view.
        gsap.set(targets, fromVars);
        return;
      }

      // Use an explicit fromTo (not gsap.from): @gsap/react's useGSAP does not
      // revert on dependency change by default, so a `from` tween would capture
      // the already-hidden `gsap.set` state above as its destination and animate
      // hidden→hidden, leaving the content invisible.
      gsap.fromTo(
        targets,
        fromVars,
        {
          autoAlpha: 1,
          x: 0,
          y: 0,
          duration,
          delay,
          ease: GSAP_EASE.out,
          stagger: stagger ?? 0,
          overwrite: 'auto',
        },
      );
    },
    {
      scope: ref,
      dependencies: [inView, reduced, from, distance, duration, delay, stagger],
    },
  );

  return (
    <Tag ref={ref} className={cn(className)}>
      {children}
    </Tag>
  );
}
